import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClientProvider, QueryClient } from "@tanstack/react-query";
import { BrowserRouter, HashRouter, Routes, Route, Navigate } from "react-router-dom";
import { Layout } from "./components/Layout";
import Dashboard from "./pages/Dashboard";
import Candidates from "./pages/Candidates";
import Companies from "./pages/Companies";
import Roles from "./pages/Roles";
import JobOffers from "./pages/JobOffers";
import Matches from "./pages/Matches";
import Lists from "./pages/Lists";
import ListDetail from "./pages/ListDetail";
import Queries from "./pages/Queries";
import PeopleSearch from "./pages/PeopleSearch";
import Analytics from "./pages/Analytics";
import QueryDetail from "./pages/QueryDetail";
import Kanban from "./pages/Kanban";
import Inbox from "./pages/Inbox";
import { TasksPage } from "./components/tasks/TasksPage";
import Calendar from "./pages/Calendar";
import CandidateDetail from "./pages/CandidateDetail";
import CandidateDuplicates from "./pages/CandidateDuplicates";
import CompanyDetail from "./pages/CompanyDetail";
import JobDetail from "./pages/JobDetail";
import Workforce from "./pages/Workforce";
import WorkforceDetail from "./pages/WorkforceDetail";
import WorkforceBenchPage from "./pages/WorkforceBenchPage";
import MatchDetail from "./pages/MatchDetail";
import NotFound from "./pages/NotFound";
import Settings from "./pages/Settings";
import AdminConsole from "./pages/AdminConsole";
import Auth from "./pages/Auth";
import MatchTest from "./pages/MatchTest";
import { NotificationTest } from "./pages/NotificationTest";
import Notifications from "./pages/Notifications";
import Transcripts from "./pages/Transcripts";
import Help from "./pages/Help";
import Onboarding from "./pages/Onboarding";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import RegisterPage from "./pages/RegisterPage";
import { SettingsProvider } from "./providers/SettingsProvider";
import { AuthProvider } from "./providers/AuthProvider";
import { FeatureFlagsProvider } from "./providers/FeatureFlagsProvider";
import { ClarityProvider } from "./providers/ClarityProvider";
import { ModalProvider } from "./contexts/ModalContext";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { OnboardingCheck } from "./components/OnboardingCheck";
import { DevModeProtectedRoute } from "./components/DevModeProtectedRoute";
import { FeatureFlagProtectedRoute } from "./components/FeatureFlagProtectedRoute";
import { useEffect } from 'react';
import { ensureJobsTableColumns } from './integrations/supabase/ensure-columns';
import AddCompanyPopup from "@/components/companies/AddCompanyPopup";
import PublicJobPage from "./pages/jobs/PublicJobPage";
import PublicJobsListPage from "./pages/jobs/PublicJobsListPage";

// Create a query client instance
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

function App() {
  useEffect(() => {
    // Ensure all required columns exist when the app loads
    ensureJobsTableColumns().catch(console.error);
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <ClarityProvider>
        <BrowserRouter>
          <Routes>
            {/* Public routes: no providers */}
            <Route path="/jobs/:id/public" element={<PublicJobPage />} />
            <Route path="/jobs/org/:orgId/public" element={<PublicJobsListPage />} />

            {/* All other routes: wrap in providers */}
            <Route
              path="/*"
              element={
                <AuthProvider>
                  <FeatureFlagsProvider>
                  <SettingsProvider>
                    <ModalProvider>
                    <TooltipProvider>
                        <Toaster />
                        <Sonner />
                        <Routes>
                      <Route path="/auth" element={<Auth />} />
                      <Route path="/register" element={<RegisterPage />} />
                      <Route path="/onboarding" element={<Onboarding />} />
                      <Route path="/reset-password" element={<ResetPasswordPage />} />
                      {<Route element={<ProtectedRoute />}>
                        <Route element={<OnboardingCheck>
                          <Layout />
                        </OnboardingCheck>}>
                          <Route path="/add-company-popup" element={<AddCompanyPopup />} />
                          <Route index element={<Dashboard />} />
                          <Route path="inbox" element={<Inbox />} />
                          <Route path="candidates" element={<Candidates />} />
                          <Route path="candidates/duplicates" element={
                            <FeatureFlagProtectedRoute feature="duplicateFinder">
                              <CandidateDuplicates />
                            </FeatureFlagProtectedRoute>
                          } />
                          <Route path="candidates/:id" element={<CandidateDetail />} />
                          <Route path="companies" element={<Companies />} />
                          <Route path="companies/:id" element={<CompanyDetail />} />
                          <Route path="roles" element={<Roles />} />
                          <Route path="jobs" element={<JobOffers />} />
                          <Route path="jobs/:id" element={<JobDetail />} />
                          <Route element={<FeatureFlagProtectedRoute feature="workforce" />}>
                            <Route path="workforce" element={<Workforce />} />
                            <Route path="workforce/bench" element={<WorkforceBenchPage />} />
                            <Route path="workforce/:id" element={<WorkforceDetail />} />
                          </Route>
                          <Route path="matches" element={<Matches />} />
                          <Route path="matches/:id" element={<MatchDetail />} />
                          <Route element={<FeatureFlagProtectedRoute feature="lists" />}>
                            <Route path="lists" element={<Lists />} />
                            <Route path="lists/:id" element={<ListDetail />} />
                          </Route>
                          <Route element={<FeatureFlagProtectedRoute feature="queries" />}>
                            <Route path="queries" element={<Queries />} />
                            <Route path="queries/:id" element={<QueryDetail />} />
                          </Route>
                          <Route path="people-search" element={
                            <FeatureFlagProtectedRoute feature="peopleSearch">
                              <PeopleSearch />
                            </FeatureFlagProtectedRoute>
                          } />
                          <Route path="kanban" element={
                            <FeatureFlagProtectedRoute feature="kanban">
                              <Kanban />
                            </FeatureFlagProtectedRoute>
                          } />
                          <Route path="tasks" element={
                            <FeatureFlagProtectedRoute feature="tasks">
                              <TasksPage />
                            </FeatureFlagProtectedRoute>
                          } />
                          <Route path="analytics" element={
                            <FeatureFlagProtectedRoute feature="analytics">
                              <Analytics />
                            </FeatureFlagProtectedRoute>
                          } />
                          <Route path="transcripts" element={
                            <FeatureFlagProtectedRoute feature="transcripts">
                              <Transcripts />
                            </FeatureFlagProtectedRoute>
                          } />
                          <Route path="calendar" element={
                            <FeatureFlagProtectedRoute feature="calendar">
                              <Calendar />
                            </FeatureFlagProtectedRoute>
                          } />
                          <Route path="match-test" element={
                            <DevModeProtectedRoute>
                              <MatchTest />
                            </DevModeProtectedRoute>
                          } />
                          <Route path="notification-test" element={
                            <DevModeProtectedRoute>
                              <NotificationTest />
                            </DevModeProtectedRoute>
                          } />
                          <Route path="settings" element={<Settings />} />
                          <Route path="admin" element={
                            <ProtectedRoute requireAdmin>
                              <AdminConsole />
                            </ProtectedRoute>
                          } />
<Route path="notifications" element={<Notifications />} />
                          <Route path="help" element={<Help />} />
                          <Route path="*" element={<NotFound />} />
                        </Route>
                      </Route>}
                      </Routes>
                    </TooltipProvider>
                  </ModalProvider>
              </SettingsProvider>
              </FeatureFlagsProvider>
              </AuthProvider>
              }
            />
          </Routes>
        </BrowserRouter>
      </ClarityProvider>
    </QueryClientProvider>
  );
}

export default App;
